// Platform Admin — final roadmap panels (ADMIN_UPGRADE_PLAN 27, 35, 37, 42).
// ReconcilePanel   money that doesn't line up
// AbusePanel       anomaly flags awaiting human review
// IntegrationPanel webhook / API / automation wiring per tenant
// ConsentPanel     one contact's full consent history, for disputes
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  reconcileInvoices, integrationHealth, consentAudit,
  listAbuseFlags, resolveAbuseFlag, scanTenantAbuse,
  type ReconcileReport, type IntegrationRow, type ConsentReport, type AbuseFlag,
} from '../../lib/padminGrowth';
import { usePadminTenants } from '../../lib/padmin';
import { useToast } from '../ui/Toast';

const CARD ='rounded-card border border-slate-200 bg-white p-5 shadow-sm';
const FLD = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';
const GHOST = 'rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-ink-2 hover:border-primary hover:text-primary disabled:opacity-40';
const BTN = 'rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40';
const TH = 'whitespace-nowrap bg-surface px-3.5 py-3 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3';

// Amounts are OMR despite the `...Usd` field names (see lib/billing.ts money()).
const usd = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 3 })} OMR`;
const when = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

function Err({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{msg}</div>;
}

/** Promise-based single-line text prompt (replaces native window.prompt). */
interface TextPromptReq {
  title: string;
  minLength: number;
  resolve: (value: string | null) => void;
}

function TextPromptModal({ req, onDone }: { req: TextPromptReq; onDone: (value: string | null) => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const ok = value.trim().length >= req.minLength;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="presentation">
      <button type="button" aria-label={t('common.cancel')} onClick={() => onDone(null)} className="absolute inset-0 bg-slate-900/40" />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="pa-growth-prompt-label"
        onSubmit={(e) => { e.preventDefault(); if (ok) onDone(value.trim()); }}
        className={cn(CARD, 'relative z-10 w-full max-w-sm shadow-lg')}
      >
        <label id="pa-growth-prompt-label" htmlFor="pa-growth-prompt" className="block text-sm font-bold text-ink">{req.title}</label>
        <input id="pa-growth-prompt" autoFocus value={value} onChange={(e) => setValue(e.target.value)} className={cn(FLD, 'mt-3')} />
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => onDone(null)} className={GHOST}>{t('common.cancel')}</button>
          <button type="submit" disabled={!ok} className={BTN}>{t('common.confirm')}</button>
        </div>
      </form>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: 'good' | 'bad' }) {
  return (
    <div className={CARD}>
      <span className="block text-[11px] font-bold uppercase tracking-wide text-ink-3">{label}</span>
      <b className={cn('mt-1 block text-2xl font-extrabold',
        tone === 'good' ? 'text-primary' : tone === 'bad' ? 'text-red-600' : 'text-ink')}>{value}</b>
    </div>
  );
}

// ── 27. Reconciliation ───────────────────────────────────────────────────────
const ISSUE_TONE: Record<string, string> = {
  duplicate_paid: 'bg-red-100 text-red-700',
  plan_mismatch: 'bg-red-100 text-red-700',
  paid_no_ref: 'bg-amber-100 text-amber-700',
  stuck_pending: 'bg-slate-100 text-ink-2',
};

export function ReconcilePanel({ onOpenTenant }: { onOpenTenant?: (id: string) => void }) {
  const { t } = useI18n();
  const [report, setReport] = useState<ReconcileReport | null>(null);
  const [days, setDays] = useState(90);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setReport(null); setErr(null);
    reconcileInvoices(days).then(setReport).catch((e: Error) => setErr(e.message));
  }, [days]);
  useEffect(load, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <b className="block text-sm text-ink">{t('pa.rec.title')}</b>
          <p className="text-xs text-ink-3">{t('pa.rec.note')}</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="ms-auto rounded-[9px] border border-slate-200 px-3 py-2 text-sm font-bold">
          <option value={30}>30d</option><option value={90}>90d</option><option value={365}>365d</option>
        </select>
        <button type="button" onClick={load} className={GHOST}>{t('pa.hl.refresh')}</button>
      </div>
      <Err msg={err} />
      {!report && !err && <div className="py-8 text-center text-sm text-ink-3">…</div>}

      {report && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label={t('pa.rec.collected')} value={usd(report.totals.collectedUsd)} tone="good" />
            <Stat label={t('pa.inv.status.paid')} value={report.totals.paid} />
            <Stat label={t('pa.inv.status.pending')} value={report.totals.pending} />
            <Stat label={t('pa.rec.issues')} value={report.issues.length}
              tone={report.issues.length > 0 ? 'bad' : undefined} />
          </div>

          <div className="rounded-card border border-slate-200 bg-white shadow-sm">
            {report.issues.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-3">{t('pa.rec.clean')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse">
                  <thead>
                    <tr>{['pa.rec.col.kind', 'pa.inv.col.tenant', 'pa.inv.col.amount', 'pa.rec.col.detail', 'pa.inv.col.created'].map((k) => (
                      <th key={k} className={TH}>{t(k)}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {report.issues.map((i) => (
                      <tr key={`${i.tenantId}/${i.invoiceId}/${i.kind}`} className="border-t border-slate-200">
                        <td className="whitespace-nowrap px-3.5 py-3">
                          <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', ISSUE_TONE[i.kind] ?? 'bg-slate-100')}>
                            {t(`pa.rec.kind.${i.kind}`)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3.5 py-3">
                          <button type="button" onClick={() => onOpenTenant?.(i.tenantId)}
                            className="text-[13px] font-semibold text-ink hover:text-primary hover:underline">{i.tenantName}</button>
                        </td>
                        <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-bold text-ink">{usd(i.amountUsd)}</td>
                        <td className="px-3.5 py-3 text-[12px] text-ink-2">{i.detail}</td>
                        <td className="whitespace-nowrap px-3.5 py-3 text-xs text-ink-3">{when(i.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── 35. Abuse flags ──────────────────────────────────────────────────────────
export function AbusePanel({ onOpenTenant }: { onOpenTenant?: (id: string) => void }) {
  const { t } = useI18n();
  const [flags, setFlags] = useState<AbuseFlag[] | null>(null);
  const [counts, setCounts] = useState({ throttle: 0, flag: 0, unacknowledged: 0 });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const [textReq, setTextReq] = useState<TextPromptReq | null>(null);
  const askText = (title: string, minLength = 1) =>
    new Promise<string | null>((resolve) => setTextReq({ title, minLength, resolve }));

  const load = useCallback(() => {
    setErr(null);
    listAbuseFlags().then((r) => { setFlags(r.flags); setCounts(r.counts); })
      .catch((e: Error) => { setErr(e.message); setFlags([]); });
  }, []);
  useEffect(load, [load]);

  async function resolve(f: AbuseFlag, action: 'acknowledge' | 'clear') {
    const reason = await askText(t(action === 'clear' ? 'pa.ab.clearPrompt' : 'pa.ab.ackPrompt'), 3);
    if (!reason) return;
    setBusy(f.tenantId); setErr(null);
    try { await resolveAbuseFlag({ tenantId: f.tenantId, action, reason }); load(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  async function rescan(f: AbuseFlag) {
    setBusy(f.tenantId); setErr(null);
    try {
      const r = await scanTenantAbuse(f.tenantId);
      const reasons = r.verdict.reasons.map((x: string) => t(`pa.ab.reason.${x}`)).join(', ') || t('pa.ab.noSignals');
      toast.info(t('pa.ab.rescanResult', { name: f.tenantName, level: r.verdict.level, score: r.verdict.score, reasons }), 8000);
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <div className="space-y-4">
      <div>
        <b className="block text-sm text-ink">{t('pa.ab.title')}</b>
        <p className="text-xs text-ink-3">{t('pa.ab.note')}</p>
      </div>
      <Err msg={err} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label={t('pa.ab.throttle')} value={counts.throttle} tone={counts.throttle > 0 ? 'bad' : undefined} />
        <Stat label={t('pa.ab.flagged')} value={counts.flag} />
        <Stat label={t('pa.ab.unreviewed')} value={counts.unacknowledged} />
      </div>

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        {flags === null ? <div className="py-8 text-center text-sm text-ink-3">…</div>
          : flags.length === 0 ? <p className="py-8 text-center text-sm text-ink-3">{t('pa.ab.empty')}</p>
            : flags.map((f) => (
              <div key={f.id} className="border-b border-slate-100 p-4 last:border-0">
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => onOpenTenant?.(f.tenantId)}
                    className="text-[13px] font-bold text-ink hover:text-primary hover:underline">{f.tenantName}</button>
                  <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold',
                    f.level === 'throttle' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700')}>
                    {t(`pa.ab.level.${f.level}`)} · {f.score}
                  </span>
                  {f.acknowledged && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-ink-3">{t('pa.ab.acknowledged')}</span>
                  )}
                  <div className="ms-auto flex gap-1.5">
                    <button type="button" disabled={busy === f.tenantId} className={GHOST} onClick={() => void rescan(f)}>{t('pa.ab.rescan')}</button>
                    {!f.acknowledged && (
                      <button type="button" disabled={busy === f.tenantId} className={GHOST} onClick={() => void resolve(f, 'acknowledge')}>{t('pa.ab.ack')}</button>
                    )}
                    <button type="button" disabled={busy === f.tenantId} className={GHOST} onClick={() => void resolve(f, 'clear')}>{t('pa.ab.clear')}</button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {f.reasons.map((r) => (
                    <span key={r} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-ink-2">{t(`pa.ab.reason.${r}`)}</span>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-ink-3">
                  {t('pa.ab.sent')}: {Number(f.signals.messagesThisMonth ?? 0).toLocaleString()} ·{' '}
                  {t('pa.ab.optouts')}: {Number(f.signals.optOutsThisMonth ?? 0).toLocaleString()} ·{' '}
                  {t('pa.ab.failed')}: {Number(f.signals.failedThisMonth ?? 0).toLocaleString()} ·{' '}
                  {t('pa.hl.col.quality')}: {String(f.signals.qualityRating ?? '—')}
                </p>
              </div>
            ))}
      </div>
      {textReq && (
        <TextPromptModal req={textReq} onDone={(v) => { setTextReq(null); textReq.resolve(v); }} />
      )}
    </div>
  );
}

// ── 37. Integration health ───────────────────────────────────────────────────
export function IntegrationPanel({ onOpenTenant }: { onOpenTenant?: (id: string) => void }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<IntegrationRow[] | null>(null);
  const [summary, setSummary] = useState({ webhooksConfigured: 0, webhooksFailing: 0, apiConsumers: 0 });
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null); setErr(null);
    integrationHealth().then((r) => { setRows(r.rows); setSummary(r.summary); })
      .catch((e: Error) => { setErr(e.message); setRows([]); });
  }, []);
  useEffect(load, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-ink-3">{t('pa.int.note')}</p>
        <button type="button" onClick={load} className={cn(GHOST, 'ms-auto')}>{t('pa.hl.refresh')}</button>
      </div>
      <Err msg={err} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label={t('pa.int.configured')} value={summary.webhooksConfigured} />
        <Stat label={t('pa.int.failing')} value={summary.webhooksFailing} tone={summary.webhooksFailing > 0 ? 'bad' : undefined} />
        <Stat label={t('pa.int.apiConsumers')} value={summary.apiConsumers} />
      </div>

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse">
            <thead>
              <tr>{['pa.inv.col.tenant', 'pa.int.col.webhook', 'pa.int.col.api', 'pa.int.col.automations', 'pa.hl.col.issue'].map((k) => (
                <th key={k} className={TH}>{t(k)}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={5} className="px-3.5 py-8 text-center text-sm text-ink-3">…</td></tr>}
              {rows?.map((r) => (
                <tr key={r.tenantId} className={cn('border-t border-slate-200', r.severity >= 50 && 'bg-red-50/50')}>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    <button type="button" onClick={() => onOpenTenant?.(r.tenantId)}
                      className="text-[13px] font-semibold text-ink hover:text-primary hover:underline">{r.tenantName}</button>
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    {!r.webhook.configured ? <span className="text-xs text-ink-3">—</span> : (
                      <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold',
                        !r.webhook.enabled ? 'bg-slate-100 text-ink-3'
                          : r.webhook.lastOk === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700')}>
                        {!r.webhook.enabled ? t('pa.int.off') : r.webhook.lastOk === false ? t('pa.int.failingOne') : t('pa.int.ok')}
                        {' · '}{r.webhook.events}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[12px] text-ink-2">
                    {r.apiKeys.live > 0 ? `${r.apiKeys.live} live` : '—'}
                  </td>
                  <td className="px-3.5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.automations.autoReply && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-ink-2">auto-reply</span>}
                      {r.automations.aiAgent && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-ink-2">AI</span>}
                      {r.automations.chatWidget && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-ink-2">widget</span>}
                    </div>
                  </td>
                  <td className="max-w-[220px] px-3.5 py-3 text-[11px] text-red-600">{r.webhook.lastError ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── 42. Consent audit ────────────────────────────────────────────────────────
export function ConsentPanel() {
  const { t } = useI18n();
  const { tenants } = usePadminTenants();
  const [tenantId, setTenantId] = useState('');
  const [phone, setPhone] = useState('');
  const [report, setReport] = useState<ConsentReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    if (!tenantId || phone.trim().length < 6) return;
    setBusy(true); setErr(null); setReport(null);
    try { setReport(await consentAudit(tenantId, phone.trim())); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  const optedOut = report?.consent.status === 'unsubscribed';

  return (
    <div className={cn(CARD, 'space-y-3')}>
      <div>
        <b className="block text-sm text-ink">{t('pa.cs.title')}</b>
        <p className="text-xs text-ink-3">{t('pa.cs.note')}</p>
      </div>
      <Err msg={err} />

      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[180px] flex-1">
          <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.inv.col.tenant')}</span>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className={FLD}>
            <option value="">—</option>
            {tenants.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        <label className="min-w-[180px] flex-1">
          <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.cs.phone')}</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void run(); }}
            placeholder="+968 7691 2321" className={FLD} />
        </label>
        <button type="button" onClick={() => void run()} disabled={busy || !tenantId || phone.trim().length < 6} className={BTN}>
          {busy ? '…' : t('pa.ov.search')}
        </button>
      </div>

      {report && (
        <div className="space-y-3 rounded-[9px] border border-slate-200 p-4">
          <div className={cn('rounded-[9px] px-3 py-2 text-sm font-semibold',
            optedOut ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700')}>
            {report.consent.summary}
          </div>

          <div className="grid gap-x-6 gap-y-1 text-xs text-ink-2 sm:grid-cols-2">
            <span>{t('pa.cs.phone')}: <b className="font-mono text-ink">{report.subscriber.phoneE164}</b></span>
            <span>{t('pa.cs.name')}: <b className="text-ink">{report.subscriber.name || '—'}</b></span>
            <span>{t('pa.cs.source')}: <b className="text-ink">{report.subscriber.source || '—'}</b></span>
            <span>{t('pa.cs.added')}: <b className="text-ink">{when(report.subscriber.createdAt)}</b></span>
            <span>{t('pa.cs.optedIn')}: <b className="text-ink">{when(report.consent.optedInAt)}</b></span>
            <span>{t('pa.cs.optedOut')}: <b className="text-ink">{when(report.consent.optedOutAt)}</b>
              {report.consent.optOutSource ? ` (${report.consent.optOutSource})` : ''}</span>
            <span>{t('pa.cs.marketing')}: <b className="text-ink">{report.consent.marketingOptIn ? '✓' : '✕'}</b></span>
            <span>{t('pa.cs.lastMsg')}: <b className="text-ink">{when(report.conversation.lastMessageAt)}</b></span>
          </div>

          <div>
            <b className="block text-xs font-bold text-ink-2">{t('pa.cs.evidence')}</b>
            {report.keywordEvents.length === 0 ? (
              <p className="mt-1 text-xs text-ink-3">{t('pa.cs.noEvidence')}</p>
            ) : (
              <ul className="mt-1 divide-y divide-slate-100">
                {report.keywordEvents.map((e) => (
                  <li key={e.id} className="flex items-center gap-2 py-1.5 text-xs">
                    <span className="font-mono text-ink-3">{when(e.createdAt)}</span>
                    <span className="font-bold text-ink-2">{e.direction}</span>
                    <span className="text-ink">{e.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
